/**
 * Calendario della biglietteria.
 *
 * Legge le varianti gia' presenti in pagina (snippets/booking-data.liquid) e
 * disegna calendario, slot e riepilogo senza una sola richiesta di rete.
 *
 * Regole non negoziabili rispettate qui:
 * - le date si costruiscono PER COMPONENTI, mai da stringa: new Date("2026-10-03")
 *   e' letta come UTC e in certi fusi restituisce il giorno prima
 * - il calendario e' un role="grid" navigabile con le frecce, non una tabella
 *   di link: quello del concorrente e' inutilizzabile da tastiera
 * - nessuna stringa e' scritta qui dentro: arrivano tutte dagli attributi
 *   data-label-*, popolati da Liquid dal file locale
 * - senza JavaScript resta il selettore nativo e il sito vende comunque
 */
class BookingCalendar extends HTMLElement {
  connectedCallback() {
    const source = this.querySelector('[data-booking-variants]');
    if (!source) return;

    try {
      const dati = JSON.parse(source.textContent);
      this.variants = Array.isArray(dati) ? dati : dati.varianti;
      this.capienza = Array.isArray(dati) ? 0 : Number(dati.capienza) || 0;
    } catch {
      return; // JSON rotto: resta il fallback nativo, che e' meglio di niente
    }
    if (!Array.isArray(this.variants) || this.variants.length === 0) return;

    this.els = {
      counters: this.querySelectorAll('[data-counter]'),
      breakdown: this.querySelector('[data-breakdown]'),
      calendar: this.querySelector('[data-calendar]'),
      monthLabel: this.querySelector('[data-month-label]'),
      prev: this.querySelector('[data-prev-month]'),
      next: this.querySelector('[data-next-month]'),
      slots: this.querySelector('[data-slots]'),
      slotsLabel: this.querySelector('[data-slots-label]'),
      summary: this.querySelector('[data-summary]'),
      variantId: this.querySelector('[data-variant-id]'),
      quantity: this.querySelector('[data-quantity]'),
      fallbackSelect: this.querySelector('.booking__fallback-select'),
      lines: this.querySelector('[data-cart-lines]'),
      submit: this.querySelector('[data-submit]'),
      phone: this.querySelector('[data-phone]'),
    };
    if (!this.els.calendar) return;

    this.labels = {
      weekdays: (this.dataset.weekdays || 'L,M,M,G,V,S,D').split(','),
      available: this.dataset.labelAvailable || '',
      soldOut: this.dataset.labelSoldOut || '',
      chooseDate: this.dataset.labelChooseDate || '',
      chooseTime: this.dataset.labelChooseTime || '',
      selected: this.dataset.labelSelected || '',
      selectedOne: this.dataset.labelSelectedOne || '',
      noSlots: this.dataset.labelNoSlots || '',
      adults: this.dataset.labelBreakdownAdults || '',
      free: this.dataset.labelBreakdownFree || '',
      reduced: this.dataset.labelBreakdownReduced || '',
      totale: this.dataset.labelBreakdownTotal || '',
      addError: this.dataset.labelAddError || '',
    };

    this.index();
    this.state = { adulti: 1, bambini: 0, date: null, time: null };

    // Non tutti i prodotti hanno un prezzo per i bambini: il Paint & Wine ha
    // un tipo solo. Lasciare il contatore li' sarebbe peggio che inutile —
    // i bambini conterebbero come persone e occuperebbero posti, ma nessuna
    // riga verrebbe aggiunta al carrello e finirebbero dentro gratis.
    this.haBambini = this.variants.some((v) => {
      const r = this.ruolo(v.k);
      return r === 'gratis' || r === 'ridotto';
    });
    if (!this.haBambini) {
      for (const g of this.els.counters) {
        if (g.dataset.counter === 'bambini') g.hidden = true;
      }
    }
    this.readUrl();

    // Il mese mostrato all'avvio e' quello della data selezionata, oppure il
    // primo mese che ha davvero dei posti: non il mese corrente, che potrebbe
    // essere vuoto e dare l'impressione che non ci sia nulla in programma.
    const start = this.state.date || this.firstAvailableDate();
    const [y, m] = start ? [start.getFullYear(), start.getMonth()] : [new Date().getFullYear(), new Date().getMonth()];
    this.view = { year: y, month: m };

    this.bind();
    this.preparaTelefono();
    this.render();

    // Segnala al CSS che il componente ha preso il controllo: da qui il
    // fallback nativo viene nascosto.
    this.setAttribute('data-ready', '');
  }

  /**
   * Il telefono obbligatorio.
   *
   * Senza JavaScript il campo si chiama `properties[Telefono]` e viaggia con
   * la variante del selettore di ripiego. Qui le righe sono `items[i][...]`,
   * e Shopify ignorerebbe una proprieta' scritta fuori dalle righe: il campo
   * visibile perde il nome e il valore viene copiato sulla prima riga da
   * `sincronizzaRighe`.
   *
   * Il campo resta `required` anche senza nome: la validazione del browser
   * vale lo stesso, ed e' lei a fermare l'invio prima che il carrello lo
   * intercetti.
   */
  preparaTelefono() {
    const tel = this.els.phone;
    if (!tel) return;
    this.proprietaTelefono = tel.dataset.phoneProperty || 'Telefono';
    tel.removeAttribute('name');

    tel.addEventListener('input', () => {
      // Il formato lo controllano le cifre, non i caratteri: «+39 333 123 4567»
      // e «3331234567» sono lo stesso numero. Fra 8 e 15 cifre (lo standard
      // internazionale ne ammette al massimo 15).
      const cifre = tel.value.replace(/\D/g, '').length;
      const valido = tel.value.trim() === '' || (cifre >= 8 && cifre <= 15);
      tel.setCustomValidity(valido ? '' : tel.dataset.phoneInvalid || '');

      const riga = this.els.lines?.querySelector('[data-phone-line]');
      if (riga) riga.value = tel.value.trim();
    });
  }

  /**
   * Il ruolo di una variante dedotto dal valore dell'opzione.
   *
   * Si guarda il testo invece di confrontarlo con una costante: se la cliente
   * rinomina "Bambino ridotto" in "Bambino 2° e successivi" dall'Admin, il
   * calendario continua a capire di cosa si tratta.
   */
  ruolo(k) {
    const s = String(k || '').toLowerCase();
    if (s.includes('gratis')) return 'gratis';
    if (s.includes('ridott')) return 'ridotto';
    return 'adulto';
  }

  /**
   * Raggruppa per data e, dentro ogni data, per fascia oraria.
   *
   * Una fascia non e' piu' una variante: e' un orario con dentro i tre tipi
   * di biglietto, che sono tre varianti distinte perche' hanno tre prezzi.
   */
  index() {
    this.byDate = new Map();
    const perChiave = new Map();

    for (const v of this.variants) {
      if (!v.d || !v.t) continue;
      const chiave = `${v.d}|${v.t}`;
      if (!perChiave.has(chiave)) {
        const fascia = { d: v.d, t: v.t, tipi: {} };
        perChiave.set(chiave, fascia);
        if (!this.byDate.has(v.d)) this.byDate.set(v.d, []);
        this.byDate.get(v.d).push(fascia);
      }
      perChiave.get(chiave).tipi[this.ruolo(v.k)] = v;
    }

    for (const list of this.byDate.values()) {
      list.sort((a, b) => String(a.t).localeCompare(String(b.t)));
    }
    this.dates = [...this.byDate.keys()].sort();
  }

  /** "2026-10-03" -> Date locale. Mai new Date(stringa). */
  toDate(iso) {
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }

  /** Date -> "2026-10-03", senza passare da toISOString, che converte in UTC. */
  toIso(date) {
    const p = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
  }

  /** Quante persone si stanno prenotando. */
  persone() {
    return this.state.adulti + this.state.bambini;
  }

  /**
   * Come si ripartiscono i bambini fra gratuiti e ridotti.
   * Per ogni adulto un bambino entra gratis; dal secondo, ridotto.
   */
  conti() {
    const gratis = Math.min(this.state.bambini, this.state.adulti);
    return { adulti: this.state.adulti, gratis, ridotti: this.state.bambini - gratis };
  }

  /**
   * Posti che restano in una fascia, in PERSONE. null = non calcolabile.
   *
   * Qui sta il nodo di tutto l'impianto. La capienza e' una sola — trenta
   * persone — ma Shopify tiene un magazzino per variante e non sa
   * condividerne uno fra tre. I tre tipi partono quindi tutti da trenta, e
   * nessuno dei tre, da solo, dice quanti posti restano: se sono entrati
   * dieci adulti e cinque bambini, il tipo "Adulto" segna venti e il tipo
   * "Bambino gratis" venticinque, mentre le persone dentro sono quindici e
   * i posti liberi quindici.
   *
   * Si ricava sottraendo alla capienza la somma dei consumi dei tre tipi.
   */
  postiRimasti(fascia) {
    const tipi = Object.values(fascia.tipi);
    if (!tipi.length) return 0;

    // Inventario non tracciato: nessun limite da far rispettare.
    if (tipi.some((v) => v.m !== 'shopify')) return null;
    if (!this.capienza) {
      // Prodotto senza la capienza dichiarata: si ripiega sul tipo adulto,
      // che e' il comportamento di prima dei tre tipi.
      const a = fascia.tipi.adulto;
      return a ? Math.max(0, Number(a.q) || 0) : 0;
    }

    const consumati = tipi.reduce((somma, v) => somma + Math.max(0, this.capienza - (Number(v.q) || 0)), 0);
    return Math.max(0, this.capienza - consumati);
  }

  /** La fascia accoglie il gruppo? Inventario non tracciato = sempre si'. */
  fits(fascia, persone) {
    const s = this.postiRimasti(fascia);
    return s === null ? true : s >= persone;
  }

  /** La data ha almeno una fascia che accoglie il gruppo? */
  dateFits(iso, persone) {
    const list = this.byDate.get(iso);
    if (!list) return false;
    if (this.isPast(iso)) return false;
    return list.some((f) => this.fits(f, persone));
  }

  isPast(iso) {
    const d = this.toDate(iso);
    if (!d) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  }

  firstAvailableDate() {
    const iso = this.dates.find((d) => this.dateFits(d, this.state ? this.persone() : 1));
    return iso ? this.toDate(iso) : null;
  }

  readUrl() {
    const params = new URLSearchParams(window.location.search);
    const date = params.get('data');
    const time = params.get('orario');
    if (date && this.byDate.has(date) && !this.isPast(date)) {
      this.state.date = this.toDate(date);
      if (time && this.byDate.get(date).some((f) => f.t === time)) {
        this.state.time = time;
      }
    }
  }

  /**
   * Aggiorna l'indirizzo con la selezione corrente.
   * replaceState e non pushState: con pushState il tasto indietro
   * ripercorrerebbe ogni singolo clic sul calendario invece di tornare alla
   * pagina precedente.
   */
  writeUrl() {
    const params = new URLSearchParams(window.location.search);
    if (this.state.date) params.set('data', this.toIso(this.state.date));
    else params.delete('data');
    if (this.state.time) params.set('orario', this.state.time);
    else params.delete('orario');
    const qs = params.toString();
    history.replaceState(null, '', qs ? `${location.pathname}?${qs}` : location.pathname);
  }

  bind() {
    this.els.prev?.addEventListener('click', () => this.shiftMonth(-1));
    this.els.next?.addEventListener('click', () => this.shiftMonth(1));

    this.els.counters.forEach((gruppo) => {
      const chiave = gruppo.dataset.counter;
      const massimo = Number(gruppo.dataset.max) || 10;
      gruppo.querySelectorAll('[data-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          // Almeno un adulto: un biglietto per bambino da solo non esiste, e
          // il regolamento chiede che i minori siano accompagnati.
          const minimo = chiave === 'adulti' ? 1 : 0;
          const n = this.state[chiave] + Number(btn.dataset.step);
          this.state[chiave] = Math.max(minimo, Math.min(massimo, n));

          // Togliendo un adulto i bambini gratuiti diminuiscono: la data
          // scelta potrebbe non reggere piu' il gruppo.
          if (this.state.date && !this.dateFits(this.toIso(this.state.date), this.persone())) {
            this.state.date = null;
            this.state.time = null;
          }
          this.render();
        });
      });
    });

    this.els.calendar.addEventListener('keydown', (e) => this.onGridKeydown(e));
  }

  shiftMonth(delta) {
    const d = new Date(this.view.year, this.view.month + delta, 1);
    this.view = { year: d.getFullYear(), month: d.getMonth() };
    this.render();
  }

  render() {
    this.renderMonth();
    this.renderSlots();
    this.renderSummary();
    this.writeUrl();
  }

  renderMonth() {
    const { year, month } = this.view;

    if (this.els.monthLabel) {
      this.els.monthLabel.textContent = new Intl.DateTimeFormat('it-IT', {
        month: 'long',
        year: 'numeric',
      }).format(new Date(year, month, 1));
    }

    // I pulsanti mese si disabilitano quando non c'e' nulla oltre: mostrare
    // mesi vuoti fa sembrare che il programma sia finito.
    const first = this.dates[0] ? this.toDate(this.dates[0]) : null;
    const last = this.dates[this.dates.length - 1] ? this.toDate(this.dates[this.dates.length - 1]) : null;
    if (this.els.prev && first) {
      this.els.prev.disabled = new Date(year, month, 1) <= new Date(first.getFullYear(), first.getMonth(), 1);
    }
    if (this.els.next && last) {
      this.els.next.disabled = new Date(year, month, 1) >= new Date(last.getFullYear(), last.getMonth(), 1);
    }

    const grid = document.createElement('div');
    grid.setAttribute('role', 'grid');
    grid.className = 'booking__grid';

    const head = document.createElement('div');
    head.setAttribute('role', 'row');
    head.className = 'booking__row booking__row--head';
    for (const w of this.labels.weekdays) {
      const cell = document.createElement('span');
      cell.setAttribute('role', 'columnheader');
      cell.className = 'booking__weekday';
      cell.textContent = w;
      head.appendChild(cell);
    }
    grid.appendChild(head);

    const firstOfMonth = new Date(year, month, 1);
    // getDay() torna 0 per domenica: in Italia la settimana inizia di lunedi'.
    const offset = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    let row = this.newRow();
    for (let i = 0; i < offset; i++) row.appendChild(this.emptyCell());

    const selectedIso = this.state.date ? this.toIso(this.state.date) : null;
    let hasFocusable = false;

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const iso = this.toIso(date);
      const inProgram = this.byDate.has(iso);
      const fits = this.dateFits(iso, this.persone());

      const cell = document.createElement('div');
      cell.setAttribute('role', 'gridcell');
      cell.className = 'booking__cell';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'booking__day';
      btn.textContent = String(day);
      btn.dataset.date = iso;
      btn.disabled = !fits;

      if (!inProgram) btn.dataset.state = 'off';
      else if (!fits) btn.dataset.state = 'full';
      else btn.dataset.state = 'free';

      const isSelected = iso === selectedIso;
      // aria-selected appartiene alla CELLA, non al pulsante: su un <button>
      // non e' un attributo ammesso e axe lo segnala come violazione critica.
      // Il pulsante usa aria-current, che su un elemento interattivo e'
      // corretto e viene annunciato come "data corrente".
      cell.setAttribute('aria-selected', String(isSelected));
      if (isSelected) btn.setAttribute('aria-current', 'date');
      // Tabindex mobile: un solo giorno e' raggiungibile con Tab, gli altri
      // con le frecce. E' il comportamento atteso da una griglia.
      const focusable = isSelected || (!hasFocusable && fits);
      btn.tabIndex = focusable ? 0 : -1;
      if (focusable) hasFocusable = true;

      const long = new Intl.DateTimeFormat('it-IT', { dateStyle: 'full' }).format(date);
      btn.setAttribute('aria-label', inProgram && fits ? long : `${long} — ${this.labels.soldOut}`);

      if (fits) {
        btn.addEventListener('click', () => {
          this.state.date = date;
          this.state.time = null;
          this.render();
        });
      }

      cell.appendChild(btn);
      row.appendChild(cell);

      if ((offset + day) % 7 === 0) {
        grid.appendChild(row);
        row = this.newRow();
      }
    }
    if (row.childElementCount > 0) {
      while (row.childElementCount < 7) row.appendChild(this.emptyCell());
      grid.appendChild(row);
    }

    this.els.calendar.replaceChildren(grid);
  }

  newRow() {
    const row = document.createElement('div');
    row.setAttribute('role', 'row');
    row.className = 'booking__row';
    return row;
  }

  emptyCell() {
    const cell = document.createElement('div');
    cell.setAttribute('role', 'gridcell');
    cell.className = 'booking__cell booking__cell--empty';
    return cell;
  }

  onGridKeydown(event) {
    const current = event.target.closest('.booking__day');
    if (!current) return;

    const steps = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    const step = steps[event.key];
    if (step === undefined) return;

    event.preventDefault();
    const days = [...this.els.calendar.querySelectorAll('.booking__day')];
    const from = days.indexOf(current);
    let i = from + step;

    // Salta i giorni non selezionabili nella direzione richiesta.
    while (i >= 0 && i < days.length && days[i].disabled) i += Math.sign(step);
    if (i < 0 || i >= days.length) return;

    days.forEach((d) => (d.tabIndex = -1));
    days[i].tabIndex = 0;
    days[i].focus();
  }

  renderSlots() {
    if (!this.els.slots) return;

    if (!this.state.date) {
      this.els.slots.replaceChildren();
      if (this.els.slotsLabel) this.els.slotsLabel.textContent = this.labels.chooseDate;
      return;
    }

    const iso = this.toIso(this.state.date);
    const list = (this.byDate.get(iso) || []).filter((f) => this.fits(f, this.persone()));

    if (this.els.slotsLabel) {
      this.els.slotsLabel.textContent = list.length ? this.labels.chooseTime : this.labels.noSlots;
    }

    const frag = document.createDocumentFragment();
    for (const v of list) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'booking__slot';
      btn.dataset.time = v.t;
      btn.setAttribute('aria-pressed', String(v.t === this.state.time));

      const time = document.createElement('span');
      time.className = 'booking__slot-time';
      time.textContent = v.t;
      btn.appendChild(time);

      const note = document.createElement('span');
      note.className = 'booking__slot-seats';
      // Nessun numero. Mostravamo i posti residui perche' informano e creano
      // urgenza in modo onesto, ma la capienza di una fascia e' un dato
      // interno della Tenuta: la cliente ha chiesto di non esporlo. Le fasce
      // piene restano comunque fuori dall'elenco, quindi cio' che si vede e'
      // prenotabile davvero.
      note.textContent = this.labels.available;
      btn.appendChild(note);

      btn.addEventListener('click', () => {
        this.state.time = v.t;
        this.render();
      });

      frag.appendChild(btn);
    }
    this.els.slots.replaceChildren(frag);
  }

  selectedFascia() {
    if (!this.state.date || !this.state.time) return null;
    const iso = this.toIso(this.state.date);
    return (this.byDate.get(iso) || []).find((f) => f.t === this.state.time) || null;
  }

  /** Le righe da mettere nel carrello: tipo, variante, quantita', prezzo. */
  righe(fascia) {
    const c = this.conti();
    return [
      { ruolo: 'adulto', n: c.adulti },
      { ruolo: 'gratis', n: c.gratis },
      { ruolo: 'ridotto', n: c.ridotti },
    ]
      .filter((r) => r.n > 0 && fascia.tipi[r.ruolo])
      .map((r) => ({ ...r, v: fascia.tipi[r.ruolo] }));
  }

  soldi(centesimi) {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(centesimi / 100);
  }

  renderSummary() {
    const fascia = this.selectedFascia();
    const c = this.conti();

    // I contatori a schermo seguono lo stato, non il contrario: cosi' un
    // valore corretto dal codice (togliendo un adulto i gratuiti calano)
    // si vede subito.
    this.els.counters.forEach((g) => {
      const out = g.querySelector('[data-count]');
      if (out) out.textContent = String(this.state[g.dataset.counter]);
    });

    // Senza tipi per bambini la ripartizione non ha niente da ripartire, e le
    // sue etichette parlano di venti euro: sul Paint & Wine direbbero il
    // falso. Il totale lo dice gia' il riepilogo.
    if (this.els.breakdown && !this.haBambini) {
      this.els.breakdown.textContent = '';
      this.els.breakdown.hidden = true;
    } else if (this.els.breakdown) {
      const pezzi = [this.labels.adults.replace('{{count}}', String(c.adulti))];
      if (c.gratis) pezzi.push(this.labels.free.replace('{{count}}', String(c.gratis)));
      if (c.ridotti) pezzi.push(this.labels.reduced.replace('{{count}}', String(c.ridotti)));

      let totale = 0;
      if (fascia) for (const r of this.righe(fascia)) totale += r.v.p * r.n;
      if (fascia) pezzi.push(this.labels.totale.replace('{{total}}', this.soldi(totale)));

      this.els.breakdown.textContent = pezzi.join(' · ');
    }

    this.sincronizzaRighe(fascia);
    if (this.els.submit) this.els.submit.disabled = !fascia;

    if (!this.els.summary) return;

    if (!fascia) {
      this.els.summary.textContent = this.state.date ? this.labels.chooseTime : this.labels.chooseDate;
      return;
    }

    const dateText = new Intl.DateTimeFormat('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(this.state.date);

    let totale = 0;
    for (const r of this.righe(fascia)) totale += r.v.p * r.n;

    // "1 persone" e' l'errore che si nota subito e fa sembrare il sito
    // automatico. Il singolare ha una frase sua, non un numero piu' un plurale.
    const persone = this.persone();
    const modello = persone === 1 && this.labels.selectedOne
      ? this.labels.selectedOne
      : this.labels.selected;

    this.els.summary.textContent = modello
      .replace('{{date}}', dateText)
      .replace('{{time}}', fascia.t)
      .replace('{{party}}', String(persone))
      .replace('{{total}}', this.soldi(totale));
  }

  /**
   * Riempie il modulo con le righe da aggiungere al carrello.
   *
   * Shopify accetta piu' righe in una sola aggiunta con i campi
   * `items[0][id]`, `items[0][quantity]`, `items[1][id]`... Usarli invece di
   * una chiamata `fetch` scritta qui significa che il carrello a tendina
   * continua a intercettare l'invio come ha sempre fatto: stessa gestione
   * degli errori, stesso ricaricamento della tendina, stessa apertura. Una
   * seconda strada per aggiungere al carrello sarebbe una seconda strada da
   * tenere allineata.
   *
   * E le tre righe entrano INSIEME: se entrassero con tre richieste separate,
   * il fallimento della seconda lascerebbe in carrello mezza prenotazione.
   */
  sincronizzaRighe(fascia) {
    const dove = this.els.lines;
    if (!dove) return;

    // I campi del ripiego senza JavaScript non devono partire insieme ai
    // nostri: `disabled` li esclude dall'invio, `hidden` no.
    for (const el of [this.els.variantId, this.els.quantity, this.els.fallbackSelect]) {
      if (el) el.disabled = true;
    }

    const righe = fascia ? this.righe(fascia) : [];
    const frag = document.createDocumentFragment();

    righe.forEach((r, i) => {
      frag.appendChild(this.campo(`items[${i}][id]`, String(r.v.id)));
      frag.appendChild(this.campo(`items[${i}][quantity]`, String(r.n)));
    });

    // Il telefono sulla sola prima riga: su tutte e tre comparirebbe tre
    // volte nell'ordine e nell'email di conferma.
    if (righe.length && this.els.phone) {
      const tel = this.campo(`items[0][properties][${this.proprietaTelefono}]`, this.els.phone.value.trim());
      tel.dataset.phoneLine = '';
      frag.appendChild(tel);
    }

    dove.replaceChildren(frag);
  }

  campo(nome, valore) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = nome;
    input.value = valore;
    return input;
  }
}

if (!customElements.get('booking-calendar')) {
  customElements.define('booking-calendar', BookingCalendar);
}
